import { useState } from "react";
import { DoorLoopLeadDto } from "@/dto";

const STORAGE_KEY = "demoFormData";

interface FormData {
  contactDetailsStep?: Pick<DoorLoopLeadDto, "email" | "name" | "phone" | "numberOfUnits">;
  userDetailsStep?: Record<string, unknown>;
}

const safeRead = (): FormData => {
  try {
    if (typeof localStorage === "undefined") return {};
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? (parsed as FormData) : {};
  } catch {
    // localStorage may be disabled (Safari ITP, private mode, browser
    // extensions, low-storage tabs) or contain corrupted JSON. Falling back
    // to empty state lets the form mount; we also try to clear the bad key
    // so subsequent loads don't keep failing.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* truly nothing we can do */
    }
    return {};
  }
};

const safeWrite = (data: FormData): void => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // QuotaExceededError on Safari private mode, or storage disabled.
    // Form continues to work in-memory — persistence is a nice-to-have,
    // never a hard requirement for completing the funnel.
  }
};

export const useFormStorage = () => {
  const [formData, setFormData] = useState<FormData>(safeRead);

  const saveStepData = (step: keyof FormData, values: Partial<DoorLoopLeadDto>) => {
    const updatedData = { ...formData, [step]: values };
    setFormData(updatedData);
    safeWrite(updatedData);
  };

  return { formData, saveStepData };
};
